import cx from 'classnames';
import moment, {Moment} from 'moment';
import {useCallback, useMemo} from 'react';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import MomentLocaleUtils from 'react-day-picker/moment';

import {Icon} from 'components';
import {useLanguage} from 'hooks';

import {FormControl} from '../FormControl';

type Props = {
    value?: Moment;
    placeholder?: string;
    timeZone?: string;
    format: string;
    hasError?: boolean;
    errorMessage?: string;
    label?: string;
    className?: string;
    overlayClassName?: string;
    minDate?: Moment;
    maxDate?: Moment;
    openDirection?: 'top' | 'bottom';
    onChange: (value: Moment) => void;
}

export const DatePicker: React.FC<Props> = ({
    value, label, timeZone, format, hasError, errorMessage, className, overlayClassName, minDate, maxDate, openDirection = 'bottom',
    onChange
}: Props) => {
    const [language] = useLanguage();
    const date = useMemo(() => value ? value.toDate() : null, [value]);
    const disabledDays = useMemo(() => ({
        before: minDate ? minDate.toDate() : null,
        after: maxDate ? maxDate.toDate() : null
    }), [maxDate, minDate]);

    let calendarRef = null;

    const onDateChange = useCallback((_date: Date) => {
        const dateMoment = moment.tz(_date, timeZone);
        dateMoment.set({hours: 0, minutes: 0, seconds: 0, milliseconds: 0});
        onChange(dateMoment);
    }, [onChange, timeZone]);

    const formatDate = useCallback((_date: Date) => MomentLocaleUtils.formatDate(_date, format, timeZone), [format, timeZone]);

    return (
        <div className={cx(className, 'tw-flex tw-w-full')}>
            <FormControl {...{
                value: date, label, className: 'tw-flex-grow', hasError, errorMessage, skipErrorMessage: !errorMessage,
            }}
            >
                {() => (
                    <div className='app-date-time tw-flex tw-w-full tw-items-center tw-bg-light'>
                        <DayPickerInput
                            ref={(el) => calendarRef = el}
                            value={date}
                            classNames={{
                                container: 'DayPickerInput tw-flex-grow tw-w-full tw-px-3',
                                overlay: 'DayPickerInput-Overlay',
                                overlayWrapper: 'DayPickerInput-OverlayWrapper',
                            }}
                            inputProps={{
                                placeholder: null,
                                readonly: 'readonly'
                            }}
                            overlayComponent={({children, onBlur, onFocus, classNames}) => (
                                <div
                                    className={cx(classNames, overlayClassName,
                                        {
                                            'tw-bottom-0': openDirection === 'top',
                                            'tw-top-0': openDirection === 'bottom'
                                        },
                                        'tw-absolute tw-bg-light tw-z-3 tw-border tw-border-default'
                                    )}
                                    onBlur={onBlur}
                                    onFocus={onFocus}
                                >
                                    {children}
                                </div>
                            )}
                            formatDate={formatDate}
                            dayPickerProps={{
                                localeUtils: MomentLocaleUtils,
                                locale: language,
                                showOutsideDays: true,
                                numberOfMonths: 1,
                                selectedDays: date,
                                disabledDays
                            }}
                            onDayChange={onDateChange}
                        />
                        <Icon
                            name='calendar'
                            color='default-dimmed'
                            className='tw-mr-2'
                            button
                            onClick={() => calendarRef.getInput().focus()}
                        />
                    </div>
                )}
            </FormControl>
        </div>
    );
};